/**
 * Twelve Data (API key): /time_series (daily bars), /eod (latest close), /symbol_search. Twelve Data is on the ASX
 * information-vendor list, so with the right plan and an ASX display licence its prices can be shown to customers.
 * Symbols are `EXCHANGE:CODE` (what `symbolFor` returns for this provider, e.g. `ASX:VAS`) and go out as
 * `symbol=VAS&exchange=ASX`; a bare symbol is sent as-is.
 */
import { z } from 'zod';
import { KeelError } from '../errors.js';
import { addDays } from '../dates.js';
import { assertDateRange, getJson, IsoDateString, NumericString, parseOrThrow, priceToCents, requireKey, } from './provider.js';
const ID = 'twelvedata';
const BASE = 'https://api.twelvedata.com';
/** Keel exchange codes → Twelve Data exchange names (the rest are the same). */
const TO_TD = { XETRA: 'XETR' };
const FROM_TD = { XETR: 'XETRA' };
const Num = z.union([NumericString, z.number()]);
const TdDate = z.string().transform((s) => s.slice(0, 10)).pipe(IsoDateString);
const ErrorBody = z.object({ status: z.literal('error'), message: z.string().optional() });
const SeriesSchema = z.object({
    meta: z.object({ currency: z.string().nullish() }),
    values: z.array(z.object({ datetime: TdDate, open: Num.nullish(), high: Num.nullish(), low: Num.nullish(), close: Num, volume: Num.nullish() })).prefault([]),
});
const EodSchema = z.object({ symbol: z.string(), exchange: z.string().nullish(), currency: z.string().nullish(), datetime: TdDate, close: Num });
const SearchSchema = z.object({
    data: z.array(z.object({
        symbol: z.string(), instrument_name: z.string(), exchange: z.string(), instrument_type: z.string().nullish(), currency: z.string().nullish(),
    })).prefault([]),
});
const securityType = (t) => {
    switch ((t ?? '').toLowerCase()) {
        case 'common stock': return 'share';
        case 'etf': return 'etf';
        case 'mutual fund': return 'managed_fund';
        case 'bond': return 'bond';
        default: return 'other';
    }
};
/** `ASX:VAS` → `symbol=VAS&exchange=ASX`; a bare symbol → `symbol=AAPL`. */
export const twelveDataSymbolQuery = (symbol) => {
    const m = /^([A-Z0-9]{2,10}):(.+)$/i.exec(symbol);
    if (!m)
        return `symbol=${encodeURIComponent(symbol)}`;
    const ex = m[1].toUpperCase();
    return `symbol=${encodeURIComponent(m[2])}&exchange=${encodeURIComponent(TO_TD[ex] ?? ex)}`;
};
/** GET a Twelve Data endpoint; its `{status:'error'}` bodies (sent with HTTP 200) become KeelError('PROVIDER'). */
export const twelveDataGet = async (ctx, path, query) => {
    const body = await getJson(ctx, ID, `${BASE}${path}?${query}&apikey=${encodeURIComponent(requireKey(ctx, ID))}`);
    const err = ErrorBody.safeParse(body);
    if (err.success)
        throw new KeelError('PROVIDER', `${ID}: ${err.data.message ?? 'request failed'}`.slice(0, 200));
    return body;
};
export const createTwelveDataProvider = (ctx) => ({
    id: ID,
    name: 'Twelve Data',
    requiresApiKey: true,
    async history(symbol, from, to) {
        assertDateRange(ID, from, to);
        // end_date is exclusive for daily bars: ask for one day more and keep only [from, to]
        const q = `${twelveDataSymbolQuery(symbol)}&interval=1day&start_date=${from}&end_date=${addDays(to, 1)}&order=asc&outputsize=5000`;
        const s = parseOrThrow(SeriesSchema, await twelveDataGet(ctx, '/time_series', q), ID);
        const ccy = s.meta.currency ?? 'USD';
        const c = (v) => (v === null || v === undefined ? null : priceToCents(v, ccy).cents);
        return s.values.filter((r) => r.datetime >= from && r.datetime <= to).map((r) => ({
            date: r.datetime, closeCents: priceToCents(r.close, ccy).cents, open: c(r.open), high: c(r.high), low: c(r.low),
            volume: r.volume === null || r.volume === undefined ? null : Number(r.volume), source: ID,
        }));
    },
    async quote(symbols) {
        const out = [];
        for (const symbol of symbols) { // ponytail: one request per symbol; batch with comma symbols if quotes get hot
            const r = parseOrThrow(EodSchema, await twelveDataGet(ctx, '/eod', twelveDataSymbolQuery(symbol)), ID);
            const { cents, currency } = priceToCents(r.close, r.currency ?? 'USD');
            out.push({ symbol, priceCents: cents, currency, asOf: `${r.datetime}T00:00:00.000Z` });
        }
        return out;
    },
    async search(q, opts = {}) {
        const body = parseOrThrow(SearchSchema, await twelveDataGet(ctx, '/symbol_search', `symbol=${encodeURIComponent(q)}&outputsize=${opts.limit ?? 10}`), ID);
        return body.data.map((r) => {
            const exchange = FROM_TD[r.exchange] ?? r.exchange;
            return {
                symbol: `${exchange}:${r.symbol}`, code: r.symbol, exchange, name: r.instrument_name,
                type: securityType(r.instrument_type), currency: r.currency ? r.currency.toUpperCase() : null,
            };
        });
    },
});
//# sourceMappingURL=twelvedata.js.map